import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

import {
  Target,
  Zap,
  Award,
  Star,
  Lightbulb,
  Globe,
  Shield,
} from 'lucide-react';

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Us - PhysicsNotebook.in</title>
        <meta name="description" content="Learn about PhysicsNotebook.in - A premier educational platform dedicated to making physics accessible, engaging, and effective for students worldwide." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Helping You Learn Physics the Smart Way
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            At Physics Notebook, we mentor students to master physics through better learning strategies and personalized guidance.
          </motion.p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
              <Target className="w-8 h-8 text-blue-600" />
              Our Mission
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              To mentor students in mastering physics through smart learning strategies — building strong conceptual clarity, speed, and accuracy needed to excel in board and competitive exams.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 mt-16">
            <div className="text-center p-6 rounded-xl hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-blue-50 to-white border border-blue-100 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Exam-Crushing Mentorship</h3>
              <p className="text-gray-700">Our expert mentors don't just teach - they train you to score. Master the art of solving 90% of exam questions with just 30% of the effort using our battle-tested problem-solving frameworks.</p>
            </div>

            <div className="text-center p-6 rounded-xl hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-green-50 to-white border border-green-100 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Zap className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Score-Boosting Strategies</h3>
              <p className="text-gray-700">Learn the exact techniques toppers use to solve problems 3x faster. Our formula-based approaches and concept maps help you recall even the most complex physics principles under exam pressure.</p>
            </div>

            <div className="text-center p-6 rounded-xl hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-purple-50 to-white border border-purple-100 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Award className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Guaranteed Results</h3>
              <p className="text-gray-700">Join thousands who've improved by 2+ grades. Our students consistently score 90%+ in board exams and secure top ranks in JEE/NEET. Your success isn't just possible - it's expected.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="w-24 h-1 bg-blue-600 mb-8"></div>
              <p className="text-gray-600 mb-6">
                Physics Notebook began with a simple but powerful idea: real learning happens through understanding, not mere instruction. We saw that traditional classroom teaching often stops at explanations — but true mastery of physics requires deeper thinking, guided practice, and the right mindset.
              </p>
              <p className="text-gray-600 mb-6">
                So we built something different. Our mentor-led approach focuses on helping students learn how to learn — developing clarity, confidence, and problem-solving skills that make even tough chapters feel manageable.
              </p>
              <p className="text-gray-600 mb-6">
                Over the years, our mentors and learning specialists have helped countless students move from rote memorization to real comprehension — improving not just their marks, but their way of thinking.
              </p>
              <p className="text-gray-600">
                Today, Physics Notebook continues to evolve, combining effective learning strategies, exam-smart guidance, and personalized mentoring — all aimed at helping students master physics with understanding that lasts a lifetime.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                <img 
                  src="/assets/logo.svg" 
                  alt="Physics Notebook Logo"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Excellence',
                description: 'We strive for the highest standards in educational content and user experience.',
                icon: Star,
                color: 'bg-yellow-50 text-yellow-600'
              },
              {
                title: 'Innovation',
                description: 'We continuously explore new ways to enhance learning through technology and pedagogy.',
                icon: Lightbulb,
                color: 'bg-blue-50 text-blue-600'
              },
              {
                title: 'Accessibility',
                description: 'We believe quality physics education should be available to all students, everywhere.',
                icon: Globe,
                color: 'bg-green-50 text-green-600'
              },
              {
                title: 'Integrity',
                description: 'We maintain the highest standards of academic integrity and ethical conduct.',
                icon: Shield,
                color: 'bg-purple-50 text-purple-600'
              }
            ].map((value, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className={`w-12 h-12 ${value.color} rounded-full flex items-center justify-center mb-4 mx-auto`}>
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-center text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-center">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Master Physics Through Better Learning?</h2>
          <p className="text-xl mb-8 text-blue-100">Join thousands of students who have transformed their approach to learning with our mentoring.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/solutions" 
              className="bg-white text-blue-600 hover:bg-blue-50 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Explore Our Solutions
            </a>
            <a 
              href="/contact" 
              className="border-2 border-white hover:bg-blue-700 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
